import {
  ITranslationSyncLanguageDetail,
  ITranslationSyncLanguageSummary,
  ITranslationSyncState,
  ITranslationSyncStatus,
} from "../../../@types/admin/AdminMasterTranslations";

/** The reference language for "missing vs English" — the API's LanguageCodes.Default. */
export const ENGLISH_LANGUAGE_CODE = "en-GB";

/** Human label for one language's sync state. */
export function syncStateLabel(state: ITranslationSyncState): string {
  switch (state) {
    case "InSync":
      return "In sync";
    case "Different":
      return "Different";
    case "OnlyInLocal":
      return "Only local";
    case "Missing":
    default:
      return "Missing";
  }
}

/** StatusBadge variant for one language's sync state. */
export function syncStateBadge(
  state: ITranslationSyncState
): "active" | "warning" | "error" | "inactive" {
  switch (state) {
    case "InSync":
      return "active";
    case "Different":
      return "warning";
    case "OnlyInLocal":
      return "inactive";
    case "Missing":
    default:
      return "error";
  }
}

export interface IKeySyncSummary {
  /** Languages the key has in master (local-only languages excluded). */
  total: number;
  inSync: number;
  different: number;
  missing: number;
  /** Languages the key has only in this environment — nothing to pull. */
  onlyInLocal: number;
  /** True when at least one master language is Different or Missing. */
  needsSync: boolean;
}

/** Rolls one key's per-language states up into the list row's summary. */
export function summarizeKeyStatus(
  status: ITranslationSyncStatus
): IKeySyncSummary {
  const count = (state: ITranslationSyncState) =>
    status.languages.filter((l) => l.state === state).length;

  const onlyInLocal = count("OnlyInLocal");
  const total = status.languages.length - onlyInLocal;
  const inSync = count("InSync");

  return {
    total,
    inSync,
    different: count("Different"),
    missing: count("Missing"),
    onlyInLocal,
    needsSync: inSync < total,
  };
}

/** "2 missing, 1 different" / "in sync" — the list row's status text. */
export function keyStatusText(summary: IKeySyncSummary): string {
  if (summary.total === 0) return "Only local";

  const parts: string[] = [];
  if (!summary.needsSync) parts.push("In sync");
  if (summary.missing > 0) parts.push(`${summary.missing} missing`);
  if (summary.different > 0) parts.push(`${summary.different} different`);
  if (summary.onlyInLocal > 0) parts.push(`${summary.onlyInLocal} only local`);
  return parts.join(", ");
}

export function keyStatusBadge(
  summary: IKeySyncSummary
): "active" | "warning" | "error" | "inactive" {
  if (summary.total === 0) return "inactive";
  if (!summary.needsSync) return "active";
  return summary.missing === summary.total ? "error" : "warning";
}

/** A clickable count of the summary table (EN-5330). */
export type SummaryColumn = Exclude<
  keyof ITranslationSyncLanguageSummary,
  "languageCode"
>;

export interface ISummaryCellFilter {
  languageCode: string;
  column: SummaryColumn;
}

/**
 * Column header / filter label. The environment-side columns carry the
 * environment's name ("In staging"), so the page reads right in every
 * environment it runs in.
 */
export function summaryColumnLabel(
  column: SummaryColumn,
  environmentName: string
): string {
  switch (column) {
    case "masterCount":
      return "Master";
    case "localCount":
      return `In ${environmentName}`;
    case "inSync":
      return "In sync";
    case "different":
      return "Different";
    case "missingLocally":
      return "Missing locally";
    case "onlyInLocal":
      return "Only in local";
    case "missingVsEnglish":
      return "Missing vs English (master)";
    case "archivedInMaster":
      return "Archived in master";
    case "archivedInLocal":
    default:
      return `Archived in ${environmentName}`;
  }
}

/**
 * Whether a key-list row is one of the rows behind a summary count (EN-5330).
 * Client mirror of TranslationsSyncService.GetSummaryAsync — keep the two in
 * step, or the count on a cell and the rows it filters to drift apart.
 */
export function matchesSummaryCell(
  status: ITranslationSyncStatus,
  { languageCode, column }: ISummaryCellFilter
): boolean {
  const cell = status.languages.find((l) => l.languageCode === languageCode);

  if (column === "archivedInMaster") return Boolean(cell?.masterArchived);
  if (column === "archivedInLocal") return Boolean(cell?.localArchived);

  // Master only: the environment's rows and archived flags play no part.
  if (column === "missingVsEnglish") {
    if (languageCode === ENGLISH_LANGUAGE_CODE) return false;
    const english = status.languages.find(
      (l) => l.languageCode === ENGLISH_LANGUAGE_CODE
    );
    const englishInMaster =
      english !== undefined &&
      english.state !== "OnlyInLocal" &&
      !english.masterArchived;
    const inMaster = cell !== undefined && cell.state !== "OnlyInLocal";
    return englishInMaster && !inMaster;
  }

  // The master-vs-environment counts cover active rows only.
  if (!cell || cell.masterArchived || cell.localArchived) return false;

  switch (column) {
    case "masterCount":
      return cell.state !== "OnlyInLocal";
    case "localCount":
      return cell.state !== "Missing";
    case "inSync":
      return cell.state === "InSync";
    case "different":
      return cell.state === "Different";
    case "missingLocally":
      return cell.state === "Missing";
    case "onlyInLocal":
      return cell.state === "OnlyInLocal";
    default:
      return false;
  }
}

const collapseWhitespace = (value: string) => value.replace(/\s+/g, " ").trim();

/**
 * Why a "Different" row is different when the two values look the same:
 * "whitespace" (only spaces / line breaks / NBSP differ) or "archived" (same
 * value, archived on one side only). Null when the values visibly differ.
 */
export function invisibleDifference(
  detail: ITranslationSyncLanguageDetail
): "whitespace" | "archived" | null {
  if (detail.state !== "Different") return null;
  const { masterValue, localValue } = detail;
  if (masterValue == null || localValue == null) return null;

  if (masterValue === localValue) {
    return detail.masterArchived !== detail.localArchived ? "archived" : null;
  }
  return collapseWhitespace(masterValue) === collapseWhitespace(localValue)
    ? "whitespace"
    : null;
}

const WHITESPACE_MARKERS: Record<string, string> = {
  " ": "·",
  " ": "⍽",
  "\t": "→",
  "\n": "↵",
  "\r": "",
};

const markWhitespace = (run: string) =>
  Array.from(run, (c) => WHITESPACE_MARKERS[c] ?? "·").join("");

/**
 * "Treatment " → "Treatment·": leading/trailing whitespace, and any inner
 * whitespace other than a single plain space, made visible.
 */
export function showWhitespace(value: string): string {
  return value.replace(/\s+/g, (run: string, offset: number) => {
    const atEdge = offset === 0 || offset + run.length === value.length;
    return atEdge || run !== " " ? markWhitespace(run) : run;
  });
}
